/**
 * The drawn 9×9 glyphs MY GAMES' doors wear (DESIGN-CHANGELOG 2026-09-03
 * "MY GAMES rail icons are drawn, not typed"; `design/GT Mine.dc.html` lines
 * 42-46 and the same block in `GT Mobile Mine.dc.html`).
 *
 * Drawn, never a font character — the same rule the bell was drawn under: as
 * stacked `◇`/`◆` glyphs the diamond's centre sat off-axis, and the fallback
 * font every Unicode glyph lands in sizes and baselines them differently. Each
 * is a `shape-rendering:crispEdges` path in a 9×9 box, so all five sit on the
 * same grid at whatever size the caller asks for.
 *
 * PATHS ARE THE DESIGN'S BYTES. `GT_LOGIC.BELL_SOLID`, `HAND_OFFER` /
 * `HAND_ITEM` and `TRI_RING` / `TRI_CORE` in `design/gt-logic.js`; the diamond
 * and star inline in the two `Mine` files. Copied, not redrawn.
 *
 * Colour: the diamond and the triangle are OUTLINES in `--gt-text` with an
 * `--acc` core — the pair a door mark beside a count wears; the star and bell
 * are `--acc`; the hand's cuff and palm are `--gt-text-4` with the offered disc
 * in `--acc`. Fixed here rather than passed in, because the design fixes them —
 * a door is not a place a caller picks a hue.
 */

import type { CSSProperties } from 'react';

import type { DoorIcon as DoorIconKind } from '../store/selectors.ts';

const DIA_RING =
  'M4 0h1v1H4zM3 1h1v1H3zM5 1h1v1H5zM2 2h1v1H2zM6 2h1v1H6zM1 3h1v1H1zM7 3h1v1H7zM0 4h1v1H0zM8 4h1v1H8zM1 5h1v1H1zM7 5h1v1H7zM2 6h1v1H2zM6 6h1v1H6zM3 7h1v1H3zM5 7h1v1H5zM4 8h1v1H4z';
const DIA_CORE = 'M4 3h1v1H4zM3 4h3v1H3zM4 5h1v1H4z';
/**
 * MY INTERESTS' mark, dressed like BACKLOG's diamond (DESIGN-CHANGELOG
 * 2026-09-16): a triangle outline with a one-cell gap and a small triangle
 * floating inside it. The solid triangle it replaced left the doors; the
 * 4-rect pixel triangle inside pixel-font CTAs is a different glyph and stays.
 */
const TRI_RING =
  'M4 0h1v1H4zM3 1h1v2H3zM5 1h1v2H5zM2 3h1v2H2zM6 3h1v2H6zM1 5h1v2H1zM7 5h1v2H7zM0 7h1v1H0zM8 7h1v1H8zM0 8h9v1H0z';
const TRI_CORE = 'M4 4h1v1H4zM3 5h3v2H3z';
const STAR = 'M4 0h1v1H4zM3 1h3v2H3zM0 3h9v1H0zM1 4h7v1H1zM2 5h5v2H2zM1 7h2v1H1zM6 7h2v1H6zM0 8h2v1H0zM7 8h2v1H7z';
/** `GT_LOGIC.BELL_SOLID` */
const BELL = 'M3 0h3v1H3zM2 1h5v2H2zM1 3h7v3H1zM0 6h9v1H0zM3 8h3v1H3z';
/** `GT_LOGIC.HAND_OFFER` / `HAND_ITEM` */
const HAND_OFFER = 'M0 6h2v3H0zM2 6h7v1H2zM2 7h6v1H2zM3 8h3v1H3z';
const HAND_ITEM = 'M3 0h3v1H3zM2 1h5v3H2zM3 4h3v1H3z';
/**
 * The party list's mark (docs/ADD-IT.md): three stacked 7-wide bars — the
 * board's rows. The one glyph here the design did not draw first; the spec
 * asked for it and named the shape, and `▦` was ruled out because it already
 * means "gallery view" on Discover. Replace the path with the mirror's bytes
 * when the design agent draws theirs.
 */
const PARTY = 'M1 1h7v1H1zM1 4h7v1H1zM1 7h7v1H1z';
/**
 * MY SCORES' door (DESIGN-CHANGELOG 2026-09-12): three ascending bars, the
 * design's bytes (`design/GT Mine.dc.html`, the `d.isBars` branch). Also the
 * glyph on GAME DETAIL's score control, both trees.
 */
const BARS = 'M0 6h2v3H0zM3 3h2v6H3zM6 0h2v9H6z';

export interface DoorIconProps {
  kind: DoorIconKind;
  /** box px — the rail draws 13, the phone strip 12.5 */
  size: number;
  style?: CSSProperties;
}

export function DoorIcon({ kind, size, style }: DoorIconProps): JSX.Element {
  const base: CSSProperties = { width: size, height: size, display: 'block', flex: 'none', ...style };
  const svg = (color: string, children: JSX.Element | JSX.Element[]) => (
    <svg viewBox="0 0 9 9" style={{ ...base, color }} shapeRendering="crispEdges" aria-hidden="true">
      {children}
    </svg>
  );
  switch (kind) {
    case 'dia':
      return svg('var(--gt-text)', [
        <path key="r" d={DIA_RING} fill="currentColor" />,
        <path key="c" d={DIA_CORE} fill="var(--acc)" />,
      ]);
    case 'tri':
      return svg('var(--gt-text)', [
        <path key="r" d={TRI_RING} fill="currentColor" />,
        <path key="c" d={TRI_CORE} fill="var(--acc)" />,
      ]);
    case 'star':
      return svg('var(--acc)', <path d={STAR} fill="currentColor" />);
    case 'bell':
      return svg('var(--acc)', <path d={BELL} fill="currentColor" />);
    case 'hand':
      return svg('var(--gt-text-4)', [
        <path key="h" d={HAND_OFFER} fill="currentColor" />,
        <path key="i" d={HAND_ITEM} fill="var(--acc)" />,
      ]);
    case 'party':
      return svg('var(--acc)', <path d={PARTY} fill="currentColor" />);
    case 'bars':
      return svg('var(--acc)', <path d={BARS} fill="currentColor" />);
  }
}

export default DoorIcon;
